import React, { useState, useEffect, useRef } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  CogIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useEquipment } from '../hooks/useEquipment';
import {
  type Equipment,
  EquipmentType,
  EquipmentStatus,
  EquipmentCondition,
  CreateEquipmentDto,
  UpdateEquipmentDto,
  EquipmentFilters,
  getEquipmentTypeLabel,
  getEquipmentStatusLabel,
  getEquipmentConditionLabel,
  getEquipmentStatusColor,
  getEquipmentConditionColor,
  formatCurrency,
  formatDate,
  formatWeight,
} from '../api/equipment';
import EquipmentForm from '../components/EquipmentForm';
import PageHero from '../components/ui/PageHero';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';

const Equipment: React.FC = () => {
  const {
    equipment,
    statistics,
    loading,
    statisticsLoading,
    error,
    pagination,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    loadEquipment,
    loadStatistics,
    setSelectedEquipment,
    selectedEquipment,
  } = useEquipment();

  // Create refs for stable function references
  const loadEquipmentRef = useRef(loadEquipment);
  const loadStatisticsRef = useRef(loadStatistics);

  // Update refs when functions change
  useEffect(() => {
    loadEquipmentRef.current = loadEquipment;
    loadStatisticsRef.current = loadStatistics;
  }, [loadEquipment, loadStatistics]);

  // State for UI
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<EquipmentFilters>({});
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Load equipment with filters
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      const newFilters = {
        ...filters,
        search: searchTerm || undefined,
      };
      loadEquipmentRef.current(newFilters);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, filters]);

  // Load initial data
  useEffect(() => {
    loadEquipmentRef.current();
    loadStatisticsRef.current();
  }, []);

  const handleCreateEquipment = async (data: CreateEquipmentDto) => {
    setFormLoading(true);
    try {
      await createEquipment(data);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating equipment:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateEquipment = async (data: UpdateEquipmentDto) => {
    if (!selectedEquipment) return;
    
    setFormLoading(true);
    try {
      await updateEquipment(selectedEquipment.id, data);
      setShowEditModal(false);
      setSelectedEquipment(null);
    } catch (error) {
      console.error('Error updating equipment:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteEquipment = async () => {
    if (!selectedEquipment) return;
    
    try {
      await deleteEquipment(selectedEquipment.id);
      setShowDeleteModal(false);
      setSelectedEquipment(null);
    } catch (error) {
      console.error('Error deleting equipment:', error);
    }
  };

  const getStatusBadgeColor = (status: EquipmentStatus) => {
    const color = getEquipmentStatusColor(status);
    const colorMap = {
      green: 'bg-emerald-100 text-emerald-800',
      yellow: 'bg-amber-100 text-amber-800',
      red: 'bg-rose-100 text-rose-800',
      gray: 'bg-violet-100 text-violet-800',
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.gray;
  };

  const getConditionBadgeColor = (condition: EquipmentCondition) => {
    const color = getEquipmentConditionColor(condition);
    const colorMap = {
      green: 'bg-emerald-100 text-emerald-800',
      blue: 'bg-cyan-100 text-cyan-800',
      yellow: 'bg-amber-100 text-amber-800',
      orange: 'bg-orange-100 text-orange-800',
      red: 'bg-rose-100 text-rose-800',
      gray: 'bg-violet-100 text-violet-800',
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.gray;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({});
  };

  const getMaintenanceStatusIcon = (equipment: Equipment) => {
    if (equipment.isMaintenanceOverdue) {
      return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
    } else if (equipment.isMaintenanceDue) {
      return <CalendarIcon className="w-4 h-4 text-yellow-500" />;
    } else {
      return <CogIcon className="w-4 h-4 text-green-500" />;
    }
  };

  if (loading && equipment.length === 0) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <PageHero
        title="Equipment"
        subtitle="Equipment inventory, maintenance, and asset tracking"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="h-5 w-5" />
            Add Equipment
          </button>
        }
      />

      {/* Statistics Overview */}
      {!statisticsLoading && statistics && (
        <div className='space-y-6'>
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
            <div className='px-6 py-4 border-b border-gray-200'>
              <div className='flex items-center'>
                <ChartBarIcon className='h-5 w-5 text-cyan-600 mr-2' />
                <h2 className='text-lg font-semibold text-slate-900'>Equipment Analytics</h2>
              </div>
            </div>
            
            <div className='p-6'>
              {/* Primary Metrics */}
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
                <div className='bg-cyan-50 p-6 rounded-lg border border-blue-200'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='text-sm font-medium text-cyan-600 mb-1'>Total Equipment</p>
                      <p className='text-3xl font-bold text-blue-900'>{statistics.totalItems}</p>
                      <p className='text-sm text-cyan-700 mt-1'>Items in inventory</p>
                    </div>
                    <div className='bg-blue-100 p-3 rounded-full'>
                      <CogIcon className='h-8 w-8 text-cyan-600' />
                    </div>
                  </div>
                </div>

                <div className='bg-green-50 p-6 rounded-lg border border-green-200'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='text-sm font-medium text-green-600 mb-1'>Total Value</p>
                      <p className='text-3xl font-bold text-green-900'>{formatCurrency(statistics.totalValue)}</p>
                      <p className='text-sm text-green-700 mt-1'>Purchase price total</p>
                    </div>
                    <div className='bg-green-100 p-3 rounded-full'>
                      <CurrencyDollarIcon className='h-8 w-8 text-green-600' />
                    </div>
                  </div>
                </div>

                <div className='bg-purple-50 p-6 rounded-lg border border-purple-200'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <p className='text-sm font-medium text-purple-600 mb-1'>Depreciated Value</p>
                      <p className='text-3xl font-bold text-purple-900'>
                        {formatCurrency(statistics.totalDepreciatedValue)}
                      </p>
                      <p className='text-sm text-purple-700 mt-1'>Current market value</p>
                    </div>
                    <div className='bg-purple-100 p-3 rounded-full'>
                      <ChartBarIcon className='h-8 w-8 text-purple-600' />
                    </div>
                  </div>
                </div>
              </div>

              {/* Status and Maintenance Metrics */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <div className='bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow'>
                  <div className='flex items-center'>
                    <div className='bg-green-100 p-2 rounded-lg mr-3'>
                      <div className='h-4 w-4 bg-green-500 rounded-full'></div>
                    </div>
                    <div>
                      <p className='text-sm font-medium text-slate-500'>Active</p>
                      <p className='text-xl font-semibold text-slate-900'>{statistics.activeItems}</p>
                    </div>
                  </div>
                </div>

                <div className='bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow'>
                  <div className='flex items-center'>
                    <div className='bg-yellow-100 p-2 rounded-lg mr-3'>
                      <CalendarIcon className='h-4 w-4 text-yellow-600' />
                    </div>
                    <div>
                      <p className='text-sm font-medium text-slate-500'>Maintenance Due</p>
                      <p className='text-xl font-semibold text-slate-900'>{statistics.maintenanceDueItems}</p>
                    </div>
                  </div>
                </div>

                <div className='bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow'>
                  <div className='flex items-center'>
                    <div className='bg-red-100 p-2 rounded-lg mr-3'>
                      <ExclamationTriangleIcon className='h-4 w-4 text-red-600' />
                    </div>
                    <div>
                      <p className='text-sm font-medium text-slate-500'>Overdue</p>
                      <p className='text-xl font-semibold text-slate-900'>{statistics.maintenanceOverdueItems}</p>
                    </div>
                  </div>
                </div>

                <div className='bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow'>
                  <div className='flex items-center'>
                    <div className='bg-blue-100 p-2 rounded-lg mr-3'>
                      <ShieldCheckIcon className='h-4 w-4 text-cyan-600' />
                    </div>
                    <div>
                      <p className='text-sm font-medium text-slate-500'>Under Warranty</p>
                      <p className='text-xl font-semibold text-slate-900'>{statistics.underWarrantyItems}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0'>
          {/* Search */}
          <div className='flex-1 max-w-lg'>
            <div className='flex items-center w-full rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'>
              <span className='pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400' aria-hidden='true'>
                <MagnifyingGlassIcon className='h-5 w-5' />
              </span>
              <input
                type='text'
                placeholder='Search equipment...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='flex-1 min-w-0 pr-4 py-2 border-0 bg-transparent focus:outline-none focus:ring-0'
              />
            </div>
          </div>

          {/* View Mode and Filter Toggle */}
          <div className='flex items-center space-x-4'>
            <div className='flex items-center bg-gray-100 rounded-lg p-1'>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <Squares2X2Icon className='h-5 w-5' />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md ${viewMode === 'table' ? 'bg-white shadow-sm' : ''}`}
              >
                <ListBulletIcon className='h-5 w-5' />
              </button>
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className='flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-slate-50/80'
            >
              <FunnelIcon className='h-5 w-5' />
              <span>Filters</span>
            </button>
          </div>
        </div>

        {showFilters && (
          <div className='mt-6 pt-6 border-t border-gray-200'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>Type</label>
                <select
                  value={filters.type || ''}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value as EquipmentType || undefined })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value=''>All Types</option>
                  {Object.values(EquipmentType).map(type => (
                    <option key={type} value={type}>
                      {getEquipmentTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value as EquipmentStatus || undefined })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value=''>All Statuses</option>
                  {Object.values(EquipmentStatus).map(status => (
                    <option key={status} value={status}>
                      {getEquipmentStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>Condition</label>
                <select
                  value={filters.condition || ''}
                  onChange={(e) => setFilters({ ...filters, condition: e.target.value as EquipmentCondition || undefined })}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                >
                  <option value=''>All Conditions</option>
                  {Object.values(EquipmentCondition).map(condition => (
                    <option key={condition} value={condition}>
                      {getEquipmentConditionLabel(condition)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-700 mb-2'>Location</label>
                <input
                  type='text'
                  value={filters.location || ''}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value || undefined })}
                  placeholder='Filter by location...'
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                />
              </div>
            </div>

            <div className='mt-4 flex justify-end'>
              <button
                onClick={clearFilters}
                className='px-4 py-2 text-sm text-slate-600 hover:text-gray-800'
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-red-800'>{error}</p>
        </div>
      )}

      {/* Equipment Grid/Table */}
      {viewMode === 'grid' ? (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
          {equipment.map((item) => (
            <div key={item.id} className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
              <div className='p-6'>
                <div className='flex justify-between items-start mb-4'>
                  <h3 className='text-lg font-semibold text-slate-900 truncate'>{item.name}</h3>
                  <div className='flex space-x-1'>
                    <button
                      onClick={() => {
                        setSelectedEquipment(item);
                        setShowViewModal(true);
                      }}
                      className='p-1 text-slate-400 hover:text-cyan-600'
                    >
                      <EyeIcon className='h-4 w-4' />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedEquipment(item);
                        setShowEditModal(true);
                      }}
                      className='p-1 text-slate-400 hover:text-green-600'
                    >
                      <PencilIcon className='h-4 w-4' />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedEquipment(item);
                        setShowDeleteModal(true);
                      }}
                      className='p-1 text-slate-400 hover:text-red-600'
                    >
                      <TrashIcon className='h-4 w-4' />
                    </button>
                  </div>
                </div>

                <div className='space-y-3'>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Type:</span>
                    <span className='text-sm font-medium'>{getEquipmentTypeLabel(item.type)}</span>
                  </div>

                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Status:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(item.status)}`}>
                      {getEquipmentStatusLabel(item.status)}
                    </span>
                  </div>

                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Condition:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getConditionBadgeColor(item.condition)}`}>
                      {getEquipmentConditionLabel(item.condition)}
                    </span>
                  </div>

                  {item.location && (
                    <div className='flex justify-between items-center'>
                      <span className='text-sm text-slate-500'>Location:</span>
                      <span className='text-sm font-medium'>{item.location}</span>
                    </div>
                  )}

                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-slate-500'>Value:</span>
                    <span className='text-sm font-medium'>{formatCurrency(item.depreciatedValue)}</span>
                  </div>

                  <div className='flex items-center justify-between'>
                    <span className='text-sm text-slate-500'>Maintenance:</span>
                    {getMaintenanceStatusIcon(item)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200'>
              <thead className='bg-slate-50/80'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Name
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Type
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Status
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Condition
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Location
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Value
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Maintenance
                  </th>
                  <th className='px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {equipment.map((item) => (
                  <tr key={item.id} className='hover:bg-slate-50/80'>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='text-sm font-medium text-slate-900'>{item.name}</div>
                      {item.model && (
                        <div className='text-sm text-slate-500'>{item.model}</div>
                      )}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                      {getEquipmentTypeLabel(item.type)}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(item.status)}`}>
                        {getEquipmentStatusLabel(item.status)}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getConditionBadgeColor(item.condition)}`}>
                        {getEquipmentConditionLabel(item.condition)}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                      {item.location || 'N/A'}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                      {formatCurrency(item.depreciatedValue)}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='flex items-center'>
                        {getMaintenanceStatusIcon(item)}
                        {item.nextMaintenanceDate && (
                          <span className='ml-2 text-sm text-slate-500'>
                            {formatDate(item.nextMaintenanceDate)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                      <div className='flex justify-end space-x-2'>
                        <button
                          onClick={() => {
                            setSelectedEquipment(item);
                            setShowViewModal(true);
                          }}
                          className='text-cyan-600 hover:text-blue-900'
                        >
                          <EyeIcon className='h-4 w-4' />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedEquipment(item);
                            setShowEditModal(true);
                          }}
                          className='text-green-600 hover:text-green-900'
                        >
                          <PencilIcon className='h-4 w-4' />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedEquipment(item);
                            setShowDeleteModal(true);
                          }}
                          className='text-red-600 hover:text-red-900'
                        >
                          <TrashIcon className='h-4 w-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className='bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-sm'>
          <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
            <div>
              <p className='text-sm text-slate-700'>
                Showing{' '}
                <span className='font-medium'>{(pagination.page - 1) * pagination.limit + 1}</span>
                {' '}to{' '}
                <span className='font-medium'>
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>
                {' '}of{' '}
                <span className='font-medium'>{pagination.total}</span>
                {' '}results
              </p>
            </div>
            <div>
              <nav className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'>
                <button
                  onClick={() => setFilters({ ...filters, page: Math.max(1, pagination.page - 1) })}
                  disabled={pagination.page === 1}
                  className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setFilters({ ...filters, page })}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.page
                          ? 'z-10 bg-cyan-50 border-blue-500 text-cyan-600'
                          : 'bg-white border-gray-300 text-slate-500 hover:bg-slate-50/80'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setFilters({ ...filters, page: Math.min(pagination.totalPages, pagination.page + 1) })}
                  disabled={pagination.page === pagination.totalPages}
                  className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <BrandModal open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidthClass="max-w-6xl">
        <BrandModalHeader title="Add New Equipment" onClose={() => setShowCreateModal(false)} />
        <BrandModalBody>
          <EquipmentForm
            onSubmit={handleCreateEquipment}
            onCancel={() => setShowCreateModal(false)}
            loading={formLoading}
          />
        </BrandModalBody>
      </BrandModal>

      {/* Edit Modal */}
      <BrandModal
        open={showEditModal && !!selectedEquipment}
        onClose={() => {
          setShowEditModal(false);
          setSelectedEquipment(null);
        }}
        maxWidthClass="max-w-6xl"
      >
        <BrandModalHeader
          title="Edit Equipment"
          onClose={() => {
            setShowEditModal(false);
            setSelectedEquipment(null);
          }}
        />
        <BrandModalBody>
          {selectedEquipment && (
            <EquipmentForm
              equipment={selectedEquipment}
              onSubmit={handleUpdateEquipment}
              onCancel={() => {
                setShowEditModal(false);
                setSelectedEquipment(null);
              }}
              loading={formLoading}
            />
          )}
        </BrandModalBody>
      </BrandModal>

      {/* View Modal */}
      <BrandModal
        open={showViewModal && !!selectedEquipment}
        onClose={() => {
          setShowViewModal(false);
          setSelectedEquipment(null);
        }}
        maxWidthClass="max-w-4xl"
      >
        <BrandModalHeader
          title="Equipment Details"
          onClose={() => {
            setShowViewModal(false);
            setSelectedEquipment(null);
          }}
        />
        <BrandModalBody>
          {selectedEquipment && (
            <>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='space-y-4'>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Name</label>
                    <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.name}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Type</label>
                    <p className='mt-1 text-sm text-slate-900'>{getEquipmentTypeLabel(selectedEquipment.type)}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Model</label>
                    <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.model || 'N/A'}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Serial Number</label>
                    <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.serialNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Asset Tag</label>
                    <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.assetTag || 'N/A'}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(selectedEquipment.status)}`}>
                      {getEquipmentStatusLabel(selectedEquipment.status)}
                    </span>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Condition</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getConditionBadgeColor(selectedEquipment.condition)}`}>
                      {getEquipmentConditionLabel(selectedEquipment.condition)}
                    </span>
                  </div>
                </div>

                <div className='space-y-4'>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Location</label>
                    <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.location || 'N/A'}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Manufacturer</label>
                    <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.manufacturer || 'N/A'}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Purchase Price</label>
                    <p className='mt-1 text-sm text-slate-900'>{formatCurrency(selectedEquipment.purchasePrice)}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Current Value</label>
                    <p className='mt-1 text-sm text-slate-900'>{formatCurrency(selectedEquipment.depreciatedValue)}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Purchase Date</label>
                    <p className='mt-1 text-sm text-slate-900'>{formatDate(selectedEquipment.purchaseDate)}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Warranty Expiration</label>
                    <p className='mt-1 text-sm text-slate-900'>{formatDate(selectedEquipment.warrantyExpiration)}</p>
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-500'>Next Maintenance</label>
                    <p className='mt-1 text-sm text-slate-900'>{formatDate(selectedEquipment.nextMaintenanceDate)}</p>
                  </div>
                </div>
              </div>

              {selectedEquipment.description && (
                <div className='mt-6'>
                  <label className='block text-sm font-medium text-slate-500'>Description</label>
                  <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.description}</p>
                </div>
              )}

              {selectedEquipment.maintenanceInstructions && (
                <div className='mt-4'>
                  <label className='block text-sm font-medium text-slate-500'>Maintenance Instructions</label>
                  <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.maintenanceInstructions}</p>
                </div>
              )}

              {selectedEquipment.notes && (
                <div className='mt-4'>
                  <label className='block text-sm font-medium text-slate-500'>Notes</label>
                  <p className='mt-1 text-sm text-slate-900'>{selectedEquipment.notes}</p>
                </div>
              )}
            </>
          )}
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={() => {
              setShowViewModal(false);
              setSelectedEquipment(null);
            }}
            className="btn btn-secondary text-sm"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => {
              setShowViewModal(false);
              setShowEditModal(true);
            }}
            className="btn btn-primary text-sm"
          >
            Edit Equipment
          </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Delete Modal */}
      <BrandModal
        open={showDeleteModal && !!selectedEquipment}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedEquipment(null);
        }}
        maxWidthClass="max-w-md"
      >
        <BrandModalHeader
          title="Delete Equipment"
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedEquipment(null);
          }}
        />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete &quot;{selectedEquipment?.name}&quot;? This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedEquipment(null);
            }}
            className="btn btn-secondary text-sm"
          >
            Cancel
          </button>
          <button type="button" onClick={handleDeleteEquipment} className="btn btn-danger text-sm">
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
};

export default Equipment; 